// Resume.jsx
// A semantic, text-first version of the résumé. This route is prerendered to
// static HTML (see scripts/prerender.js), so the full content ships in the
// initial HTML response and remains readable by crawlers and screening tools.
import React, { useEffect } from 'react';
import './Resume.css';
import SiteHeader from '../Components/SiteHeader';

const PDF_PATH = '/Illya_Bakurov_Resume.pdf';

const Role = ({ title, org, orgUrl, domains, dates, note, children }) => {
    const isExternal = orgUrl?.startsWith('http');

    return (
        <article className="resume-role">
            <div className="resume-role-head">
                <h3 className="resume-role-title">
                    <span>{title}</span>
                    <span aria-hidden="true"> · </span>
                    {orgUrl ? (
                        <a
                            href={orgUrl}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                        >
                            {org}
                        </a>
                    ) : <span>{org}</span>}
                    {domains && domains.length > 0 && (
                        <span className="resume-role-domains">
                            {domains.map((domain, index) => (
                                <React.Fragment key={domain}>
                                    {index > 0 && ' · '}
                                    <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">{domain}</a>
                                </React.Fragment>
                            ))}
                        </span>
                    )}
                </h3>
                <span className="resume-role-dates">{dates}</span>
            </div>
            {note && <p className="resume-role-note">{note}</p>}
            {children}
        </article>
    );
};

const Resume = () => {
    useEffect(() => {
        const previousTitle = document.title;
        const description = document.querySelector('meta[name="description"]');
        const previousDescription = description?.getAttribute('content');
        const canonical = document.querySelector('link[rel="canonical"]');
        const previousCanonical = canonical?.getAttribute('href');
        const title = 'Illya Bakurov | Senior Full-Stack & iOS Engineer';
        const pageDescription = 'Résumé of Illya Bakurov, a senior full-stack and iOS engineer with design-engineering and technical-leadership experience across TypeScript, React, Swift, AI, data, and cloud systems.';
        const pageUrl = 'https://ibakurov.com/resume';
        const metaUpdates = [
            ['meta[property="og:title"]', title],
            ['meta[property="og:description"]', pageDescription],
            ['meta[property="og:url"]', pageUrl],
            ['meta[name="twitter:title"]', title],
            ['meta[name="twitter:description"]', pageDescription],
        ].map(([selector, value]) => {
            const element = document.querySelector(selector);
            const previousValue = element?.getAttribute('content');
            element?.setAttribute('content', value);
            return [element, previousValue];
        });

        document.title = title;
        description?.setAttribute('content', pageDescription);
        canonical?.setAttribute('href', pageUrl);

        return () => {
            document.title = previousTitle;
            if (previousDescription) description?.setAttribute('content', previousDescription);
            if (previousCanonical) canonical?.setAttribute('href', previousCanonical);
            metaUpdates.forEach(([element, previousValue]) => {
                if (element && previousValue) element.setAttribute('content', previousValue);
            });
        };
    }, []);

    return (
        <div className="resume-page">
            <SiteHeader currentPage="resume" />
            <main className="resume">
                <header className="resume-header">
                    <div>
                        <h1>Illya Bakurov</h1>
                        <p className="resume-identity">Senior Full-Stack &amp; iOS Engineer · TypeScript, React, React Native, Swift, and AI Systems</p>
                        <p className="resume-contact">
                            <span>Los Angeles, CA</span>
                            <span aria-hidden="true"> · </span>
                            <a href="mailto:bakurov.illya@gmail.com">bakurov.illya@gmail.com</a>
                            <span aria-hidden="true"> · </span>
                            <a href="https://ibakurov.com" target="_blank" rel="noopener noreferrer">ibakurov.com</a>
                            <span aria-hidden="true"> · </span>
                            <a href="https://linkedin.com/in/ibakurov" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                            <span aria-hidden="true"> · </span>
                            <a href="https://github.com/ibakurov" target="_blank" rel="noopener noreferrer">GitHub</a>
                        </p>
                    </div>
                    <a href={PDF_PATH} className="resume-download" target="_blank" rel="noopener noreferrer">Download PDF</a>
                </header>

                <section className="resume-section">
                    <h2>Summary</h2>
                    <p>
                        Senior full-stack and iOS engineer with 11 years building and operating consumer and developer
                        products across web, native Apple platforms, AI, data, and cloud systems. Combines hands-on
                        engineering with product prototyping, design engineering, and technical leadership. Designed
                        the working prototype for a Snap feature used by 64M people daily, led products reaching 250K+
                        monthly users, and built Stella Foster for 40,000+ users. Integrated AI models for vision,
                        audio, and text directly through APIs before coding agents became mainstream, and now
                        orchestrate multi-agent development workflows with Claude Code, Codex, and Conductor.
                    </p>
                </section>

                <section className="resume-section">
                    <h2>Experience</h2>

                    <Role
                        title="Co-Founder & Founding Engineer"
                        org="Santa's AI Lab"
                        orgUrl="/santas-ai-lab"
                        domains={['santasailab.com', 'stellafoster.com', 'outsideagent.ai']}
                        dates="Oct 2023 to Sep 2026"
                        note="Primary hands-on full-stack engineer across client applications, backend services, integrations, data, reliability, and cloud delivery."
                    >
                        <ul>
                            <li><strong>Multi-surface product:</strong> built and launched Stella Foster across voice, SMS, web, React Native mobile apps, and Slack, reaching 40,000+ users.</li>
                            <li><strong>Web and native clients:</strong> built React and TypeScript customer experiences and an operations console for live conversations, tool calls, execution state, production traces, failures, and human handoffs. Built Swift and SwiftUI clients for iPhone, iPad, and Mac, plus a React Native messaging client for iOS and Android with CallKit and an Apple Watch extension.</li>
                            <li><strong>Backend and developer platform:</strong> built Stella's Node.js and Express backend on Google Cloud and Firestore, then migrated it to TypeScript. Built Outside Agent using tRPC, Zod, event-sourced PostgreSQL, Drizzle, pgvector, and MCP tooling for web, SMS, voice, and email.</li>
                            <li><strong>Memory and retrieval:</strong> built identity, preference, and working-memory tiers in Firestore with AI-estimated lifetimes for temporary facts and conflict-aware updates. Stored Gemini embeddings for conversation history in Firestore.</li>
                            <li><strong>Integrations and real-time voice:</strong> connected 15+ platforms across communications, payments, commerce, and transportation, including Twilio Voice and SMS, Stripe, Twilio Pay, Amazon, Uber Health, Slack, Google, and OpenAI. Re-platformed voice from Deepgram and ElevenLabs to OpenAI Realtime and then Gemini Live behind a provider interface supporting interruption and multilingual switching.</li>
                            <li><strong>Durable commerce:</strong> replaced contending jobs with one durable Temporal workflow per conversation. Built a stored-value ledger with Firebase and Firestore transactions, migrated it to PostgreSQL for Outside Agent, and made Stripe and Twilio Pay actions idempotent with spend controls, auditable state, and two-step voice confirmation.</li>
                            <li><strong>Cloud delivery and observability:</strong> operated Docker services on Google Cloud Run and Cloud SQL with Pub/Sub, Cloud Tasks, Cloud Scheduler, GitHub Actions, Cloud Deploy, and Terraform. Instrumented behavior with PostHog and gated releases with automated tests and Temporal workflow replay.</li>
                            <li><strong>Team growth and quality:</strong> grew the company from two founders to nine across engineering, QA, and DevOps while remaining hands-on. Hired and mentored contributors, ran a QA internship program across three cohorts, and led the QA team in building a 1,620+ case evaluation and red-team program with Promptfoo and Langfuse.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Senior Software Engineer & Advisor"
                        org="Feels"
                        orgUrl="/experience/feels"
                        domains={['feelsmusic.com']}
                        dates="Oct 2022 to Oct 2023"
                    >
                        <ul>
                            <li><strong>Full-stack delivery:</strong> led iOS, Android, and web delivery for a music platform; built the native iOS consumer app and React and Node.js artist platform hands-on, and coordinated the external Android team to keep features and releases aligned.</li>
                            <li><strong>Creator and content tooling:</strong> built a React and Node.js console for managing media, artwork, platform presence, content workflows, and engagement analytics, with ShazamKit audio recognition.</li>
                            <li><strong>Search and team growth:</strong> prototyped machine-learning-assisted search and media synchronization, moved engineering in-house, helped hire engineers, and mentored engineering interns.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Camera Lead Design Engineer"
                        org="Snap Inc."
                        orgUrl="/experience/snap"
                        dates="Apr 2020 to Sep 2022"
                        note="Designed and built fully functional product prototypes. Production engineers wrote the shipping code, and I partnered with them through launch and measurement."
                    >
                        <ul>
                            <li>Designed and built the working Objective-C prototype for the <strong>Preview Filter Carousel</strong>, used by 64M people daily, then partnered through launch. Experiments increased both filter impressions and sends across iOS and Android.</li>
                            <li>Built functional prototypes for Voice Over, Auto-Caption, Text-to-Speech, Crop, Stickers, Lens Explorer, Lens Carousel, and editing flows using Swift, Objective-C, UIKit, Figma, Origami, and ProtoPie.</li>
                            <li>Used product analytics, anonymized logs, controlled experiments, and user research to refine creation, engagement, and discovery flows before production investment.</li>
                            <li>Led and mentored three design engineers across Camera, Creative Tools, and Memories while partnering with product, design, research, data science, and engineering on product behavior, instrumentation, technical tradeoffs, and launch decisions.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Senior iOS Developer & Tech Lead"
                        org="RBC Wealth Management"
                        orgUrl="/experience/rbc-wealth-management"
                        dates="Dec 2017 to Apr 2020"
                    >
                        <ul>
                            <li>Led Objective-C and Swift development for wealth-management applications serving 25,000+ daily users under bank-grade availability, security, privacy, and compliance controls.</li>
                            <li>Worked with product, design, security, compliance, and executives across RBC, City National Bank, and IBM to translate financial workflows into reliable mobile experiences and bring the City National Bank application in-house.</li>
                            <li>Both products won Best Private Bank for Digital Client Communication in North America at the inaugural Global Wealth Tech Awards.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Co-Founder & Head of Mobile Development"
                        org="Story Star"
                        orgUrl="/experience/story-star"
                        dates="Dec 2015 to May 2020"
                    >
                        <ul>
                            <li>Built and led development across 20+ consumer iOS and web products spanning social networks, audiobooks, video editing, utilities, and games.</li>
                            <li>The largest product reached 250K+ monthly active users, and the most profitable reached $20K+ per month.</li>
                            <li>Built Spotlight, which was acquired by Snap and became a core Snapchat feature.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Senior iOS Developer"
                        org="Kinetic Commerce"
                        orgUrl="/experience/kinetic-commerce"
                        dates="Sep 2017 to Dec 2017"
                    >
                        <ul>
                            <li>Built native iPad and macOS applications for connected in-store retail experiences, balancing customer, associate, device, and commerce-system constraints.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Gameplay Programmer"
                        org="Turbo Rocket Games"
                        orgUrl="/experience/turbo-rocket-games"
                        dates="2016"
                    >
                        <ul>
                            <li>Wrote ShiVa and Lua gameplay systems for Dragon Sim Online, a live multiplayer title that won Google's AdMob Student App Challenge and passed 500K+ downloads.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Co-Founder"
                        org="Eventlime"
                        orgUrl="/experience/eventlime"
                        dates="2015 to 2017"
                    >
                        <ul>
                            <li>Co-founded an early-stage product venture during graduate school, working from idea to a working product and learning the operating loop between product choices, delivery, feedback, and team constraints.</li>
                        </ul>
                    </Role>

                    <Role
                        title="Graduate Teaching Assistant"
                        org="Brock University"
                        orgUrl="/experience/brock-university"
                        dates="2015 to 2017"
                    >
                        <ul>
                            <li>Supported students in Python, algorithms, and computer networking while completing my MSc. Delivered product-design lectures based on Don Norman's <em>The Design of Everyday Things</em>, translating design principles into practical product critique.</li>
                        </ul>
                    </Role>

                    <Role
                        title="iOS Developer"
                        org="Agency SM.Art"
                        orgUrl="/experience/agency-sm-art"
                        dates="2014 to 2016"
                    >
                        <ul>
                            <li>Built native iOS applications for banking and insurance clients in Kyiv, establishing a foundation in trust-sensitive workflows, information hierarchy, and reliable mobile delivery.</li>
                        </ul>
                    </Role>
                </section>

                <section className="resume-section">
                    <h2>Capabilities &amp; Technical Stack</h2>
                    <dl className="resume-skills">
                        <div><dt>Languages</dt><dd>TypeScript, JavaScript, Swift, Objective-C, SQL; prior production Lua and graduate teaching experience with Python</dd></div>
                        <div><dt>Web and cross-platform</dt><dd>React, React Native, Next.js, customer applications, operations consoles, and cross-platform mobile delivery</dd></div>
                        <div><dt>Apple platforms</dt><dd>iOS, iPadOS, watchOS, macOS; SwiftUI, UIKit, Foundation, CallKit, AVFoundation, ShazamKit, widgets, system search, and notification extensions</dd></div>
                        <div><dt>Apple tooling</dt><dd>URLSession, Alamofire, Swift Package Manager, CocoaPods, Xcode, and automated tests</dd></div>
                        <div><dt>Backend and APIs</dt><dd>Node.js, Express, tRPC, Zod, REST APIs, Temporal, event-sourced state, MCP servers</dd></div>
                        <div><dt>Data</dt><dd>PostgreSQL, Drizzle, pgvector, Firebase, Firestore, NoSQL, ACID ledger design</dd></div>
                        <div><dt>Cloud and CI/CD</dt><dd>GCP, Pub/Sub, Cloud Tasks, Cloud Scheduler, Cloud Run, Cloud SQL, GitHub Actions, Docker, Cloud Deploy, Terraform, KMS, Workload Identity Federation</dd></div>
                        <div><dt>Quality</dt><dd>Automated tests, Temporal workflow replay, Promptfoo, Langfuse, PostHog, production traces, release gates</dd></div>
                        <div><dt>Integrations and AI</dt><dd>Twilio Voice and SMS, Twilio Pay, Stripe, Amazon, Uber Health, Slack, OpenAI Realtime, Gemini Live, Deepgram, ElevenLabs, Google's Agent Development Kit, Claude Code, Codex, Conductor, GitHub Apps</dd></div>
                        <div><dt>Design and leadership</dt><dd>Product discovery, interaction design, functional prototyping, technical direction, hiring, mentoring, vendor management, and cross-functional delivery</dd></div>
                    </dl>
                </section>

                <section className="resume-section">
                    <h2>Education</h2>
                    <ul>
                        <li><strong>MSc Computer Science, Brock University.</strong> Springer-published research on GPU-accelerated genetic programming, up to 6x faster than the CPU implementation.</li>
                        <li><strong>BASc Computational and Applied Mathematics, National University of Kyiv-Mohyla Academy.</strong></li>
                        <li><strong>Post-Graduate Certificates, University of Toronto.</strong> Financial Analysis and Investment Management; Financial Trading and Option Strategies.</li>
                    </ul>
                </section>
            </main>
        </div>
    );
};

export default Resume;
